<template>
  <ModalDialog
    header-text="Schedules"
    class="assignments-modal"
    @close-modal="closeModal"
    open
  >
    <template v-slot:content>
      <p>
        By accessing this [OU webpage], the Orthodox Union is providing you with
        information that pertains to your inspection assignment(s). You
        acknowledge that you may use this information only to perform your
        assignment(s). You may only share this information with OU Kashrus staff
        who are involved with your plant assignment(s). You may not share this
        information with an OU customer/company. You may not use this
        information in any manner that is outside the scope of your
        assignment(s). By downloading and/or emailing this information, you
        acknowledge and agree that you are in compliance and will continue to
        comply with the above guidelines.
      </p>
      <div class="buttons-container">
        <Button
          type="button"
          theme-color="primary"
          @click="downloadSchedulesAsync"
          :disabled="loading"
        >
          Download Schedules
        </Button>
        <Button
          type="button"
          theme-color="primary"
          @click="emailSchedulesAsync"
          :disabled="loading"
        >
          Email Schedules
        </Button>
      </div>
    </template>
  </ModalDialog>
</template>

<script setup lang="ts">
import ModalDialog from "@/shared/ui/modal/ModalDialog.vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { ref } from "vue";

const main = useMainStore();
const notificationStore = useNotificationStore();

const props = defineProps<{
  ownsIds: number[];
}>();

const emit = defineEmits<{
  (e: "closeModal"): void;
}>();

const loading = ref<boolean>(false);

const downloadSchedulesAsync = async function () {
  if (loading.value) return;

  loading.value = true;

  const downloadPromise = api.postDownload("DownloadAssignmentSchedules", {
    UserLogin: main.userLogin,
    CompanyLogin: main.companyLogin,
    ownsIds: props.ownsIds.map((id) => id.toString()),
  });

  await handleResponsePromiseAsync(
    downloadPromise,
    () => {
      notificationStore.notify({
        message: "Downloaded schedules successfully",
        type: NotificationTypeEnum.success,
        duration: 5000,
      });
    },
    "AssignmentSchedulesModal.vue",
    "downloadSchedulesAsync",
    () => {
      notificationStore.notify({
        message: "Failed to download schedules",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const emailSchedulesAsync = async function () {
  if (loading.value) return;

  loading.value = true;

  const emailPromise = api.get("EmailIngredientandProductLists", {
    userLogin: main.userLogin,
    ownsList: props.ownsIds.join(";"),
  });

  await handleResponsePromiseAsync(
    emailPromise,
    (data) => {
      if (data)
        notificationStore.notify({
          message: "Emailed schedules successfully",
          type: NotificationTypeEnum.success,
          duration: 5000,
        });
      else
        notificationStore.notify({
          message: "Failed to email schedules",
          type: NotificationTypeEnum.error,
          duration: 5000,
        });
    },
    "AssignmentSchedulesModal.vue",
    "emailSchedulesAsync",
    () => {
      notificationStore.notify({
        message: "Failed to email schedules",
        type: NotificationTypeEnum.error,
        duration: 5000,
      });
    }
  );

  loading.value = false;
};

const closeModal = function () {
  emit("closeModal");
};
</script>

<style lang="scss">
.assignments-modal {
  max-width: 600px;

  .buttons-container {
    display: flex;
    gap: 16px;
    margin: 32px 0 16px 8px;
  }
}
</style>
